import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import {
  DISPOSABLE_DOMAINS,
  MIN_FILL_MS,
  RATE_LIMIT_MAX,
  RATE_LIMIT_WINDOW_MS,
} from "@/lib/waitlist.constants";
import { rsvpWindowFor, VTX_PREFERENCES, type RsvpEvent } from "@/lib/rsvps.functions";
import type { RacebandGridChannel, VideoProtocol } from "@/lib/rf-grid";

/** Public site origin, used for links inside text messages. */
const SITE_PAGE_URL = "https://squadflight.com";

/**
 * Frictionless RSVP — email required, mobile optional.
 *
 * Clicking “I’m going” immediately puts the attendee in. Account access, payment,
 * profile completion, and message engagement never change attendance.
 */

const EVENT_COLS =
  "id, tenant_id, slug, title, subtitle, description, location, starts_at, ends_at, cover_image_url, partiful_url, status, capacity, visibility, requires_freq_allocation, rsvp_opens_at, rsvp_closes_at, is_intro_session, price_cents, kit_price_cents, kit_label, payment_link_url, payment_link_with_kit_url, accepted_pass_kinds, discount_payment_link_url";

async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

function clientIp(): string {
  const headers = getRequest().headers;
  return (
    headers.get("cf-connecting-ip") ??
    headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
    "unknown"
  );
}

/** "daniel@gmail.com" → "d•••@gmail.com" — enough to recognise, not to harvest. */
function maskEmail(value: string | null): string {
  const [local = "", domain = ""] = (value ?? "").split("@");
  if (!domain) return "your other address";
  return `${local.slice(0, 1)}•••@${domain}`;
}

export const guestRsvpInputSchema = z.object({
  slug: z.string().trim().min(1).max(120),
  /** Flying (pilot seat) or spectating (free, no channel). */
  role: z.enum(["pilot", "spectator"]).optional(),
  /** Email is required while text delivery is pending; mobile remains encouraged. */
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(7).max(32).optional(),
  name: z.string().trim().max(120).optional(),
  vtxPreference: z.enum(VTX_PREFERENCES).nullable().optional(),
  guestCount: z.number().int().min(0).max(6).default(0),
  /** Token from a friend's invite link — seats them together. */
  inviteToken: z.string().trim().min(20).max(200).optional(),
  /** Short share code from a pilot's referral link (/r/CODE or ?ref=CODE). */
  refCode: z.string().trim().max(32).optional(),

  /** Proving Ground free pass code from /start. */
  passCode: z.string().trim().max(32).optional(),
  /** Text-message consent — the RSVP form requires it for phone-first holds. */
  smsConsent: z.boolean().optional().default(false),
  /**
   * Answer to "is this you?" when a new email arrives under a name we already
   * know. "existing" keeps them on their original record, "new" makes a second.
   */
  identityChoice: z.enum(["existing", "new"]).optional(),
  /** Honeypot — humans never see this field. */
  company: z.string().optional(),
  elapsedMs: z.number().int().nonnegative().default(0),
});

export const submitGuestRsvp = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => guestRsvpInputSchema.parse(data))
  .handler(async ({ data }) => {
    const guest = await import("@/lib/rsvp-guest.server");
    const { recordAttempt } = await import("@/lib/attempts.server");
    const ip = clientIp();
    const userAgent = getRequest().headers.get("user-agent");

    const log = (
      step: string,
      outcome: "started" | "ok" | "rejected" | "error",
      reason?: string,
      detail?: Record<string, unknown>,
    ) =>
      recordAttempt({
        kind: "rsvp",
        step,
        outcome,
        ...(reason ? { reason } : {}),
        ...(data.email ? { email: data.email } : {}),
        eventSlug: data.slug,
        ip,
        userAgent,
        detail: { elapsedMs: data.elapsedMs, ...(detail ?? {}) },
      });

    const reject = async (step: string, reason: string, message: string): Promise<never> => {
      await log(step, "rejected", reason);
      throw new Error(message);
    };

    await log("submit", "started");

    // 1. Honeypot — pretend success so bots learn nothing.
    if (data.company && data.company.trim().length > 0) {
      await log("honeypot", "rejected", "honeypot_filled");
      return {
        ok: true as const,
        status: "going",
        identityCheck: null as { maskedEmail: string; firstName: string } | null,
        seatLine: "",
        rsvpId: "",
        needsEmail: false,
        needsPhone: false,
        firstName: null as string | null,
        knownEmail: null as string | null,
        knownPhone: null as string | null,
        earnedSc: 0,
        standing: { xp: 0, clout: 0, credits: 0 },
      };
    }
    // 2. A very fast submit is autofill far more often than it is a bot, so it
    //    no longer blocks the RSVP — the honeypot is what stops bots. We only
    //    note it so the pattern stays visible in the attempt log.
    if (data.elapsedMs < MIN_FILL_MS) {
      await log("timing_floor", "ok", "fast_submit_allowed");
    }

    // Reassignable: claiming an existing record moves confirmation to the
    // address we already hold for that pilot.
    let email = data.email ? data.email.toLowerCase() : null;
    if (email) {
      const domain = email.split("@")[1] ?? "";
      if (DISPOSABLE_DOMAINS.has(domain)) {
        await reject("email_domain", "disposable_domain", "Please use a permanent email address.");
      }
    }
    const { toE164 } = await import("@/lib/sms.server");
    const phone = toE164(data.phone);
    if (data.phone && !phone) {
      await reject("phone_format", "bad_phone", "That mobile number doesn't look right.");
    }

    const db = await admin();

    // 3. Per-IP rate limit, shared with the waitlist form.
    if (ip !== "unknown") {
      const since = new Date(Date.now() - RATE_LIMIT_WINDOW_MS).toISOString();
      const { count } = await db
        .from("signup_attempts")
        .select("id", { count: "exact", head: true })
        .eq("ip", ip)
        .gte("created_at", since);
      if ((count ?? 0) >= RATE_LIMIT_MAX) {
        await reject(
          "rate_limit",
          "ip_rate_limited",
          "Too many requests from this connection. Try again in a bit.",
        );
      }
      await db.from("signup_attempts").insert({ ip } as never);
    }

    const { data: eventRow } = await db
      .from("events")
      .select(EVENT_COLS)
      .eq("slug", data.slug)
      .maybeSingle();
    if (!eventRow) {
      await reject("event_lookup", "event_not_found", "Could not find that event.");
    }
    const event = eventRow as unknown as RsvpEvent & { tenant_id: string };

    const window = rsvpWindowFor(event);
    if (!window.open) {
      await reject(
        "rsvp_window",
        window.reason ?? "closed",
        window.reason === "not_open_yet"
          ? "RSVPs for this event have not opened yet."
          : window.reason === "cancelled"
            ? "This event was cancelled."
            : "RSVPs for this event are closed.",
      );
    }

    // 3b. Invite link, if there is one. It only counts for this event.
    const invites = await import("@/lib/invites.server");
    const invite = data.inviteToken ? await invites.findInviteByToken(data.inviteToken) : null;
    const validInvite = invite && invite.event_id === event.id ? invite : null;

    // 4. Silent match: an existing pilot record wins, no matter how old.
    //    We never tell the page "you already have an account" — the email
    //    confirmation is what proves the address is theirs.
    const { data: matchedId } = await db.rpc("find_pilot_by_contact", {
      // A phone-first RSVP must stay on the phone-linked record even when the
      // supplied email belongs to a separate record awaiting verified merge.
      _email: phone ? null : email,
      _phone: phone,
    } as never);

    let pilotId = (matchedId as string | null) ?? null;

    // A returning pilot may give us a mobile number we have never seen. The
    // phone lookup misses, but their email is already on a record, so fall back
    // to the email match instead of trying to create a duplicate row.
    if (!pilotId && phone && email) {
      const { data: byEmail } = await db.rpc("find_pilot_by_contact", {
        _email: email,
        _phone: null,
      } as never);
      pilotId = (byEmail as string | null) ?? null;
    }

    const nameParts = (data.name ?? "").trim().split(/\s+/).filter(Boolean);

    // A returning pilot who types a second email address used to become a
    // stranger: a duplicate record and a duplicate seat for the same night.
    // When the name matches someone we already know, ask before splitting them.
    if (!pilotId && nameParts.length >= 2) {
      const { looksLikeSameName, normalizeEmail } = await import("@/lib/dedupe");
      const givenFirst = nameParts[0] ?? "";
      const givenLast = nameParts.slice(1).join(" ");
      const { data: candidateRows } = await db
        .from("pilots")
        .select("id, first_name, last_name, email, phone_normalized, date_of_birth, pilot_no")
        .eq("tenant_id", event.tenant_id)
        .is("archived_at", null)
        .is("merged_into_pilot_id", null)
        .eq("is_test", false)
        .not("email", "is", null)
        .ilike("last_name", givenLast)
        .limit(20);
      const candidates = (candidateRows ?? []) as {
        id: string;
        first_name: string;
        last_name: string;
        email: string | null;
        phone_normalized: string | null;
        date_of_birth: string | null;
      }[];
      const match = candidates.find(
        (c) =>
          normalizeEmail(c.email) !== normalizeEmail(email) &&
          looksLikeSameName(
            { first_name: givenFirst, last_name: givenLast, phone_normalized: phone ?? null },
            c,
          ),
      );

      if (match && data.identityChoice !== "new") {
        if (!data.identityChoice) {
          await log("identity_check", "ok", "name_match", { pilotId: match.id });
          const masked = maskEmail(match.email);
          return {
            ok: true as const,
            status: "identity_check",
            identityCheck: { maskedEmail: masked, firstName: match.first_name },
            seatLine: "",
            rsvpId: "",
            needsEmail: false,
            needsPhone: false,
            firstName: match.first_name as string | null,
            knownEmail: null as string | null,
            knownPhone: null as string | null,
            earnedSc: 0,
            standing: { xp: 0, clout: 0, credits: 0 },
          };
        }
        // "Yes, that's me" — stay on the original record and confirm to the
        // address that record already trusts, not the new one.
        pilotId = match.id;
        if (match.email) email = match.email.toLowerCase();
        await log("identity_check", "ok", "claimed_existing", { pilotId: match.id });
      } else if (match) {
        await log("identity_check", "ok", "declined_existing", { pilotId: match.id });
      }
    }

    if (!pilotId) {
      const { data: created, error: createError } = await db
        .from("pilots")
        .insert({
          tenant_id: event.tenant_id,
          first_name: nameParts[0] ?? "Pilot",
          // Last name is required in the database; a one-field RSVP has none
          // yet, so we store a blank and fill it in when they tell us.
          last_name: nameParts.slice(1).join(" "),
          ...(email ? { email } : {}),
          ...(phone ? { phone } : {}),
          // RSVP-only people remain leads; RSVP itself never mints a pilot number.
          lifecycle_stage: "lead",
          lead_source: validInvite ? "friend_invite" : "event_rsvp",
          lead_source_raw: validInvite ? `invite:${event.slug}` : `event:${event.slug}`,
        } as never)
        .select("id")
        .single();
      if (createError || !created) {
        console.error("guest rsvp pilot create failed", createError);
        await log("pilot_create", "error", createError?.message ?? "insert_failed");
        throw new Error("Could not save your RSVP right now.");
      }
      pilotId = (created as { id: string }).id;
      await log("pilot_create", "ok", "new_pilot", { pilotId });
    } else {
      // Silent dedupe: fill in whichever contact detail we were missing,
      // never overwrite one they already gave us.
      const { data: knownRow } = await db
        .from("pilots")
        .select("email, phone")
        .eq("id", pilotId)
        .maybeSingle();
      const known = knownRow as { email: string | null; phone: string | null } | null;
      const fill: Record<string, string> = {};
      if (email && !known?.email) fill["email"] = email;
      if (phone && !known?.phone) fill["phone"] = phone;
      if (Object.keys(fill).length) {
        await db
          .from("pilots")
          .update(fill as never)
          .eq("id", pilotId);
      }
    }
    if (pilotId && nameParts.length) {
      // Fill in a blank name, never overwrite a real one.
      await db
        .from("pilots")
        .update({ first_name: nameParts[0], last_name: nameParts.slice(1).join(" ") } as never)
        .eq("id", pilotId)
        .eq("last_name", "")
        .eq("first_name", "Pilot");
    }
    if (pilotId && data.smsConsent) {
      // Consent is sticky: ticked once, it stays on the pilot record.
      await db
        .from("pilots")
        .update({ sms_consent: true } as never)
        .eq("id", pilotId);
    }

    // A pilot's share link: link the two people for life and note the source.
    if (pilotId && data.refCode) {
      try {
        const referrals = await import("@/lib/referrals.server");
        const inviter = await referrals.resolveReferralCode(db, data.refCode);
        if (inviter && inviter.pilotId !== pilotId) {
          await referrals.linkReferral(db, {
            inviterPilotId: inviter.pilotId,
            inviteePilotId: pilotId,
            tenantId: event.tenant_id,
            eventId: event.id,
            source: "invite_link",
          });
        }
      } catch (e) {
        console.error("referral link failed", e);
      }
    }

    const { data: pilotRow } = await db
      .from("pilots")
      .select(
        "id, first_name, primary_goggle, preferred_raceband, fallback_raceband, default_video_protocol",
      )
      .eq("id", pilotId)
      .maybeSingle();
    const pilot = pilotRow as {
      first_name: string | null;
      primary_goggle: string | null;
      preferred_raceband: RacebandGridChannel;
      fallback_raceband: RacebandGridChannel;
      default_video_protocol: VideoProtocol | null;
    };

    // 4b. Free pass or discount code, checked before anything is written so a
    //     bad code never leaves half an RSVP behind.
    let passId: string | null = null;
    let passDiscountCents = 0;
    if (data.passCode && data.passCode.trim()) {
      const pass = await import("@/lib/intro-pass.server");
      const check = await pass.checkIntroPass(db, {
        code: data.passCode,
        pilotId: pilotId!,
        eventIsIntroSession: Boolean((event as { is_intro_session?: boolean }).is_intro_session),
        acceptedKinds:
          (event as { accepted_pass_kinds?: string[] | null }).accepted_pass_kinds ?? null,
      });
      if (!check.ok) {
        await reject("pass_check", check.reason, pass.passMessage(check.reason));
      }
      passId = check.ok ? check.passId : null;
      passDiscountCents = check.ok ? check.discountCents : 0;
    }

    // 5. Capacity: pilots plus their spectators.
    const spectator = data.role === "spectator";
    const { data: goingRows } = await db
      .from("event_rsvps")
      .select("guest_count, attend_role, pilot_id")
      .eq("event_id", event.id)
      .eq("status", "going");
    const allGoing = (goingRows ?? []) as {
      guest_count: number;
      attend_role: string | null;
      pilot_id: string | null;
    }[];
    const going = allGoing.filter((r) => r.attend_role !== "spectator");
    const otherRows = allGoing.filter((r) => r.pilot_id !== pilotId);
    const spectatorsTaken =
      otherRows.filter((r) => r.attend_role === "spectator").length +
      otherRows.reduce((s, r) => s + (r.guest_count ?? 0), 0);

    const { data: existingRow } = await db
      .from("event_rsvps")
      .select("id, status, guest_count, guest_email, guest_phone")
      .eq("event_id", event.id)
      .eq("pilot_id", pilotId)
      .maybeSingle();
    const prior = existingRow as {
      id: string;
      status: string;
      guest_count: number;
      guest_email: string | null;
      guest_phone: string | null;
    } | null;

    const freed = prior?.status === "going" ? 1 + (prior.guest_count ?? 0) : 0;
    const projected =
      going.length +
      going.reduce((s, r) => s + (r.guest_count ?? 0), 0) -
      freed +
      1 +
      data.guestCount;
    // Paid sessions hold no seat until the card clears. Filling the form is
    // recorded as real interest ("invited") — no seat, no capacity, no channel.
    const priceCents = Math.max(0, Number(event.price_cents ?? 0));
    // A code with no dollar value, or one worth the whole ticket, is a full pass.
    const fullComp =
      Boolean(passId) && (passDiscountCents === 0 || passDiscountCents >= priceCents);
    const { needsPaymentBeforeSeat } = await import("@/lib/rsvp-placement");
    const needsPaymentFirst = needsPaymentBeforeSeat({
      priceCents,
      fullComp,
      role: spectator ? "spectator" : "pilot",
    });
    const status = spectator
      ? spectatorsTaken + 1 > Math.max(0, Number(event.spectator_capacity ?? 0))
        ? "waitlisted"
        : "going"
      : needsPaymentFirst
        ? "invited"
        : projected > event.capacity
          ? "waitlisted"
          : "going";

    const vtx = (data.vtxPreference ??
      pilot?.default_video_protocol ??
      null) as VideoProtocol | null;
    const row = {
      tenant_id: event.tenant_id,
      event_id: event.id,
      pilot_id: pilotId,
      status,
      vtx_preference: vtx,
      guest_count: spectator ? 0 : data.guestCount,
      source: "native",
      attend_role: spectator ? "spectator" : "pilot",
      guest_email: email ?? prior?.guest_email ?? null,
      guest_phone: phone,
      guest_name: data.name?.trim() || null,
      sms_consent: data.smsConsent,
      freq_status:
        !spectator && event.requires_freq_allocation && status === "going"
          ? "pending"
          : "not_required",
      responded_at: new Date().toISOString(),
    };

    let savedId = prior?.id ?? null;
    if (prior) {
      const { error } = await db
        .from("event_rsvps")
        .update(row as never)
        .eq("id", prior.id);
      if (error) {
        console.error("guest rsvp update failed", error);
        await log("rsvp_save", "error", error.message ?? "update_failed");
        throw new Error("Could not save your RSVP.");
      }
    } else {
      const { data: inserted, error } = await db
        .from("event_rsvps")
        .insert(row as never)
        .select("id")
        .single();
      if (error?.code === "23505") {
        const { data: retry } = await db
          .from("event_rsvps")
          .update(row as never)
          .eq("event_id", event.id)
          .eq("pilot_id", pilotId)
          .select("id")
          .single();
        savedId = (retry as { id: string } | null)?.id ?? null;
      } else if (error || !inserted) {
        console.error("guest rsvp insert failed", error);
        await log("rsvp_save", "error", error?.message ?? "insert_failed");
        throw new Error("Could not save your RSVP.");
      } else {
        savedId = (inserted as { id: string }).id;
      }
    }
    if (!savedId) {
      await log("rsvp_save", "error", "no_row_id");
      throw new Error("Could not save your RSVP.");
    }
    await log("rsvp_save", "ok", status, { rsvpId: savedId, pilotId });

    // The seat exists, so now spend the code: a full pass comps the seat, a
    // discount code only reduces what they owe.
    if (passId) {
      const pass = await import("@/lib/intro-pass.server");
      const claimed = await pass.claimIntroPass(db, { passId, eventId: event.id });
      if (!claimed) await reject("pass_claim", "already_used", "That code has already been used.");

      if (passDiscountCents > 0) {
        await db
          .from("event_rsvps")
          .update({ discount_cents: passDiscountCents, promo_code_id: passId } as never)
          .eq("id", savedId);
        await log("pass_claim", "ok", "discount_applied", { rsvpId: savedId });
      }
      if (fullComp) {
        await db
          .from("event_rsvps")
          .update({
            payment_state: "credited",
            payment_marked_at: new Date().toISOString(),
          } as never)
          .eq("id", savedId);
        await db.from("rsvp_payment_log").insert({
          tenant_id: event.tenant_id,
          rsvp_id: savedId,
          to_state: "credited",
          note: "Proving Ground intro pass redeemed",
        } as never);
        await log("pass_claim", "ok", "redeemed", { rsvpId: savedId });
      }
    }

    // 6. Seat them right now. Clicking “I’m going” is the attendance decision.
    //    An invited friend sits with whoever invited them.
    let seat: { kind: string; [k: string]: any } | null = null;
    if (!spectator && event.requires_freq_allocation && status === "going") {
      const seating = await import("@/lib/rsvp-seating.server");
      const session = await seating.ensureSessionForEventServer(event as never);
      const seatArgs = {
        tenantId: event.tenant_id,
        sessionId: session.id,
        sessionDate: session.session_date,
        maxConcurrentPilots: session.max_concurrent_pilots,
        pilotId: pilotId!,
        preferred: pilot?.preferred_raceband ?? ("R1" as const),
        fallback: pilot?.fallback_raceband ?? ("R8" as const),
        protocol: vtx,
      };
      seat =
        validInvite?.inviter_pilot_id && vtx !== "FLY_AND_SWAP"
          ? await seating.seatInviteeWithInviter({
              ...seatArgs,
              inviterPilotId: validInvite.inviter_pilot_id,
            })
          : await seating.seatPilotForRsvp({ ...seatArgs, awaitingGear: vtx === "FLY_AND_SWAP" });
      await db
        .from("event_rsvps")
        .update({
          freq_status:
            seat.kind === "seated"
              ? "seated"
              : seat.kind === "blocked_hard_link"
                ? "blocked_hard_link"
                : "pending",
        } as never)
        .eq("id", savedId);
    }

    if (validInvite && validInvite.status !== "confirmed") {
      await invites.markInvite(validInvite.id, {
        status: "rsvped",
        rsvped_at: new Date().toISOString(),
      });
    }

    const seatParts = event.requires_freq_allocation
      ? guest.seatPartsFor(seat, { goggle: pilot?.primary_goggle ?? null, protocol: vtx })
      : null;
    const seatLine = seatParts
      ? `${seatParts.seat} — ${seatParts.goggles}`
      : event.requires_freq_allocation
        ? "No channel needed for this event."
        : "No channel needed for this event.";

    const { gearNoteFor, gearUnknown } = await import("@/lib/gear");

    // The message has to match the outcome: a full night means the waiting
    // list, not "you're on the grid with a channel".
    const emailKind = guest.rsvpEmailKindFor(status);

    // Only add a gear note when we don't know what they're flying. If we know,
    // the YOUR GOGGLES panel already says exactly what to set.
    const gearNote =
      status === "going" && event.requires_freq_allocation && gearUnknown(vtx)
        ? gearNoteFor(vtx)
        : null;

    // Our own checkout on the session page — the old hosted links are retired.
    const paymentUrl = needsPaymentFirst
      ? `${SITE_PAGE_URL}/events/${event.slug}#seat-checkout`
      : null;

    // This durable link manages this booking; opening it never determines attendance.
    if (email) {
      const token = await guest.issueRsvpAccess({
        tenantId: event.tenant_id,
        rsvpId: savedId,
        pilotId: pilotId,
        email,
      });

      const classSession = Boolean(
        (event as { is_intro_session?: boolean }).is_intro_session ||
        (event as { event_type?: string }).event_type === "proving_ground",
      );

      if (spectator && status === "going") {
        const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
        await sendTemplateEmail("event-rsvp-spectating", email, {
          templateData: {
            firstName: pilot?.first_name ?? nameParts[0] ?? undefined,
            eventTitle: event.title,
            startsAt: event.starts_at,
            location: event.location ?? undefined,
            eventUrl: `${SITE_PAGE_URL}/events/${event.slug}`,
          },
          idempotencyKey: `rsvp-spectating-${savedId}`,
          replyTo: guest.REPLY_TO,
        }).catch((e) => console.error("spectator confirmation failed", e));
      } else if (needsPaymentFirst) {
        // No seat yet: the note has to say so and hand them the checkout.
        const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
        await sendTemplateEmail("rsvp-payment-nudge", email, {
          templateData: {
            firstName: pilot?.first_name ?? nameParts[0] ?? null,
            eventTitle: event.title,
            startsAt: event.starts_at,
            location: event.location,
            amountDue: `$${(priceCents / 100).toFixed(2)}`,
            paymentUrl,
            eventUrl: `${SITE_PAGE_URL}/events/${event.slug}`,
            stage: "first",
          },
          idempotencyKey: `rsvp-invited-${savedId}`,
        }).catch((e) => console.error("finish-reservation email failed", e));
      } else
        await guest.sendRsvpEmail(emailKind, email, {
          gearNote: classSession ? null : gearNote,
          firstName: pilot?.first_name ?? nameParts[0] ?? null,
          eventTitle: event.title,
          startsAt: event.starts_at,
          location:
            (status === "going"
              ? await (
                  await import("@/lib/arrival-notice.server")
                ).releasedVenueAddress(db, event.id, event.starts_at)
              : null) ?? event.location,
          seatLine: classSession ? null : status === "going" && !seatParts ? seatLine : null,
          seatPart: classSession ? null : status === "going" && seatParts ? seatParts.seat : null,
          gogglesPart: classSession
            ? null
            : status === "going" && seatParts
              ? seatParts.goggles
              : null,
          accountUrl: `${SITE_PAGE_URL}/auth`,
          cancelUrl: `${SITE_PAGE_URL}/rsvp/cancel?token=${token}`,
          paymentUrl,
          classSession,
          priceCents: event.price_cents ?? null,
        });
    }

    // The first "going" RSVP earns Squad Credits — currencies accrue from the
    // moment the spot is claimed, never retroactively on an update.
    let earnedCredits = 0;
    if (status === "going" && (!prior || prior.status !== "going")) {
      const { emitEconomyEvent, economyDeltaFor } = await import("@/lib/economy.server");
      const delta = economyDeltaFor({
        kind: "rsvp_confirmed",
        pilotId: pilotId!,
        eventId: event.id,
      });
      await emitEconomyEvent({
        kind: "rsvp_confirmed",
        pilotId: pilotId!,
        eventId: event.id,
        credits: delta.credits,
      });
      earnedCredits = delta.credits;
    }

    if (phone && data.smsConsent) {
      const { sendSms } = await import("@/lib/sms.server");
      const link = paymentUrl ?? `${SITE_PAGE_URL}/events/${event.slug}`;
      await sendSms({
        tenantId: event.tenant_id,
        pilotId,
        eventId: event.id,
        to: phone,
        template: "rsvp_confirmation",
        body:
          status === "going"
            ? `SquadFlight: You're in for ${event.title}! You earned ${earnedCredits} SC. Details: ${link} Reply STOP to opt out. Msg&Data rates may apply.`
            : `SquadFlight: you're on the waiting list for ${event.title}. We'll text if a seat opens. Details: ${SITE_PAGE_URL}/events/${event.slug}`,
        idempotencyKey: `rsvp-going-${savedId}`,
      });
    }

    // Attribution: this event's own campaign gets the touch.
    const { recordTouch } = await import("@/lib/touches.server");
    await recordTouch(db as never, {
      pilotId: pilotId!,
      eventId: event.id,
      touchType: "rsvp",
      note: `RSVP (${status}) for ${event.title}`,
    });

    // Returning pilots see their standing rather than an "already exists" error.
    const { data: standingRow } = await db
      .from("pilots")
      .select("first_name, email, phone, xp_points, community_clout, squad_credits")
      .eq("id", pilotId)
      .maybeSingle();
    const standing = standingRow as {
      first_name: string | null;
      email: string | null;
      phone: string | null;
      xp_points: number | null;
      community_clout: number | null;
      squad_credits: number | null;
    } | null;

    return {
      ok: true as const,
      status,
      identityCheck: null as { maskedEmail: string; firstName: string } | null,
      seatLine,
      rsvpId: savedId,
      /** Squad Credits earned by this RSVP, shown on the result screen. */
      earnedSc: earnedCredits,
      /** Step 2 asks for the email only when we don't have one yet. */
      needsEmail: !standing?.email && !(email ?? prior?.guest_email),
      /** Offer the missing mobile number without making it a condition of attendance. */
      needsPhone: !standing?.phone && !(phone ?? prior?.guest_phone),
      firstName: standing?.first_name ?? nameParts[0] ?? null,
      knownEmail: standing?.email ?? email ?? prior?.guest_email ?? null,
      knownPhone: standing?.phone ?? phone ?? prior?.guest_phone ?? null,
      standing: {
        xp: standing?.xp_points ?? 0,
        clout: standing?.community_clout ?? 0,
        credits: standing?.squad_credits ?? 0,
      },
    };
  });

/**
 * Add an email after the attendee is already in.
 *
 * Nothing here can cost them attendance. If the email already belongs to another
 * person record we keep it on the RSVP and let the duplicate queue sort it out
 * rather than merging two people on a public form.
 */
export const attachGuestRsvpEmail = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        rsvpId: z.string().uuid(),
        email: z.string().trim().email().max(255),
        company: z.string().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    if (data.company && data.company.trim().length > 0) return { ok: true as const };

    const email = data.email.toLowerCase();
    const domain = email.split("@")[1] ?? "";
    if (DISPOSABLE_DOMAINS.has(domain)) {
      throw new Error("Please use a permanent email address.");
    }

    const db = await admin();
    const guest = await import("@/lib/rsvp-guest.server");

    const { data: rsvpRow } = await db
      .from("event_rsvps")
      .select("id, tenant_id, event_id, pilot_id, status, guest_email")
      .eq("id", data.rsvpId)
      .maybeSingle();
    const rsvp = rsvpRow as {
      id: string;
      tenant_id: string;
      event_id: string;
      pilot_id: string | null;
      status: string;
      guest_email: string | null;
    } | null;
    if (!rsvp || !rsvp.pilot_id) throw new Error("We could not find that RSVP.");

    await db
      .from("event_rsvps")
      .update({ guest_email: email } as never)
      .eq("id", rsvp.id);

    const { data: ownerId } = await db.rpc("find_pilot_by_contact", {
      _email: email,
      _phone: null,
    } as never);
    const owner = (ownerId as string | null) ?? null;

    if (!owner) {
      await db
        .from("pilots")
        .update({ email } as never)
        .eq("id", rsvp.pilot_id)
        .is("email", null);
    }

    const { data: eventRow } = await db
      .from("events")
      .select(
        "id, tenant_id, title, slug, starts_at, location, requires_freq_allocation, price_cents, payment_link_url, event_type, is_intro_session",
      )
      .eq("id", rsvp.event_id)
      .maybeSingle();
    const event = eventRow as {
      title: string;
      starts_at: string;
      location: string | null;
      price_cents: number | null;
      payment_link_url: string | null;
      event_type: string | null;
      is_intro_session: boolean | null;
    } | null;

    const token = await guest.issueRsvpAccess({
      tenantId: rsvp.tenant_id,
      rsvpId: rsvp.id,
      pilotId: rsvp.pilot_id,
      email,
    });

    const { data: pilotRow } = await db
      .from("pilots")
      .select("first_name")
      .eq("id", rsvp.pilot_id)
      .maybeSingle();

    await guest.sendRsvpEmail(guest.rsvpEmailKindFor(rsvp.status), email, {
      gearNote: null,
      firstName: (pilotRow as { first_name: string | null } | null)?.first_name ?? null,
      eventTitle: event?.title ?? "your session",
      startsAt: event?.starts_at ?? new Date().toISOString(),
      location:
        (rsvp.status === "going" && event
          ? await (
              await import("@/lib/arrival-notice.server")
            ).releasedVenueAddress(db, rsvp.event_id, event.starts_at)
          : null) ??
        event?.location ??
        null,
      seatLine: null,
      seatPart: null,
      gogglesPart: null,
      accountUrl: `${SITE_PAGE_URL}/auth`,
      cancelUrl: `${SITE_PAGE_URL}/rsvp/cancel?token=${token}`,
      classSession: Boolean(event?.is_intro_session || event?.event_type === "proving_ground"),
      priceCents: event?.price_cents ?? null,
      paymentUrl:
        rsvp.status === "going" && (event?.price_cents ?? 0) > 0
          ? (event?.payment_link_url ?? null)
          : null,
    });

    return { ok: true as const };
  });

/**
 * Add the optional mobile number after the attendee is already in. If another pilot already
 * owns it, keep it on this RSVP for later verified consolidation rather than
 * merging identities from a public form.
 */
export const attachGuestRsvpPhone = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        rsvpId: z.string().uuid(),
        phone: z.string().trim().min(7).max(32),
        smsConsent: z.literal(true),
        company: z.string().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    if (data.company && data.company.trim().length > 0) return { ok: true as const };

    const { toE164 } = await import("@/lib/sms.server");
    const phone = toE164(data.phone);
    if (!phone) throw new Error("That mobile number doesn't look right.");

    const db = await admin();
    const { data: rsvpRow } = await db
      .from("event_rsvps")
      .select("id, pilot_id")
      .eq("id", data.rsvpId)
      .maybeSingle();
    const rsvp = rsvpRow as { id: string; pilot_id: string | null } | null;
    if (!rsvp?.pilot_id) throw new Error("We could not find that RSVP.");

    const { error: rsvpError } = await db
      .from("event_rsvps")
      .update({ guest_phone: phone, sms_consent: true } as never)
      .eq("id", rsvp.id);
    if (rsvpError) throw new Error("We could not save that mobile number.");

    const { data: ownerId } = await db.rpc("find_pilot_by_contact", {
      _email: null,
      _phone: phone,
    } as never);
    const owner = (ownerId as string | null) ?? null;
    if (!owner || owner === rsvp.pilot_id) {
      await db
        .from("pilots")
        .update({ phone, sms_consent: true } as never)
        .eq("id", rsvp.pilot_id)
        .is("phone", null);
    }

    return { ok: true as const, phone };
  });

/* ------------------------------------------------------------------ *
 * "Actually, I can't make it" — cancelling without an account
 * ------------------------------------------------------------------ */

/**
 * Cancel an RSVP straight from the link in the email.
 *
 * Email-only sign-ups have no login and previously had no way out but to
 * email us, so a full night stayed full. The private link in their RSVP email
 * identifies exactly one RSVP and acts as the cancel key.
 */
export const cancelRsvpByToken = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        token: z.string().min(10),
        /** Why they are leaving — shapes the crew alert and the win-back. */
        reason: z.enum(["cant_make_it", "another_night"]).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { findRsvpAccess } = await import("@/lib/rsvp-guest.server");
    const record = await findRsvpAccess(data.token);
    if (!record) throw new Error("That cancellation link is not valid.");
    const db = await admin();
    const { data: rsvpRow } = await db
      .from("event_rsvps")
      .select("id, event_id, pilot_id, status")
      .eq("id", record.rsvp_id)
      .maybeSingle();
    if (!rsvpRow) throw new Error("We could not find that RSVP.");
    const rsvp = rsvpRow as {
      id: string;
      event_id: string;
      pilot_id: string | null;
      status: string;
    };

    const { data: eventRow } = await db
      .from("events")
      .select("id, tenant_id, slug, title, starts_at, location, capacity, requires_freq_allocation")
      .eq("id", rsvp.event_id)
      .maybeSingle();
    if (!eventRow) throw new Error("We could not find that event.");
    const event = eventRow as {
      id: string;
      tenant_id: string;
      slug: string;
      title: string;
      starts_at: string;
      location: string | null;
      capacity: number;
      requires_freq_allocation: boolean;
    };

    const already = rsvp.status === "cancelled";

    if (!already) {
      await db
        .from("event_rsvps")
        // Someone who cancels is not at the door, even if a
        // marshal had already ticked them in — leave no stale check-in behind.
        .update({ status: "cancelled", freq_status: "not_required", checked_in_at: null } as never)
        .eq("id", rsvp.id);

      if (event.requires_freq_allocation && rsvp.pilot_id) {
        const { releaseSeatAndRescan } = await import("@/lib/rsvp-seating.server");
        await releaseSeatAndRescan({
          tenantId: event.tenant_id,
          eventId: event.id,
          pilotId: rsvp.pilot_id,
        }).catch((e) => console.error("seat release failed", e));
      }

      const { fillFreedSeats } = await import("@/lib/waitlist-promote.server");
      await fillFreedSeats(db, event, { limit: 1, reason: "seat-freed" }).catch((e) =>
        console.error("waitlist fill failed", e),
      );

      const { confirmCancellation } = await import("@/lib/rsvp-cancel.server");
      await confirmCancellation(db, rsvp.id, event.title);

      // Log it on the night's campaign, and page the crew when the notice is
      // short enough that a same-day message can still save the pilot.
      const { noteCancellation } = await import("@/lib/late-cancel-alert.server");
      await noteCancellation(db, { rsvpId: rsvp.id, reason: data.reason ?? "unknown" });
    }

    return {
      ok: true,
      already,
      eventSlug: event.slug,
      eventTitle: event.title,
      eventStartsAt: event.starts_at,
    };
  });

/**
 * What the cancel link is about to cancel.
 *
 * The old page asked "cancel your RSVP?" with no idea which night it meant, so
 * a pilot could cancel the wrong Friday by accident. This names the session and
 * offers the other nights instead.
 */
export const getRsvpCancelDetails = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => z.object({ token: z.string().min(10) }).parse(data))
  .handler(async ({ data }) => {
    const { findRsvpAccess } = await import("@/lib/rsvp-guest.server");
    const record = await findRsvpAccess(data.token);
    if (!record) throw new Error("That cancellation link is not valid.");
    const db = await admin();
    const { data: rsvpRow } = await db
      .from("event_rsvps")
      .select("id, event_id, status, guest_count, guest_name")
      .eq("id", record.rsvp_id)
      .maybeSingle();
    if (!rsvpRow) throw new Error("We could not find that RSVP.");
    const rsvp = rsvpRow as {
      id: string;
      event_id: string;
      status: string;
      guest_count: number;
      guest_name: string | null;
    };

    const { data: eventRow } = await db
      .from("events")
      .select("id, slug, title, starts_at, tenant_id")
      .eq("id", rsvp.event_id)
      .maybeSingle();
    if (!eventRow) throw new Error("We could not find that event.");
    const event = eventRow as {
      id: string;
      slug: string;
      title: string;
      starts_at: string;
      tenant_id: string;
    };

    const { data: pilotRow } = await db
      .from("pilots")
      .select("first_name, preferred_name")
      .eq("id", record.pilot_id)
      .maybeSingle();
    const pilot = pilotRow as { first_name: string | null; preferred_name: string | null } | null;

    // The other nights, so "I can't make Friday" doesn't have to mean "never".
    const { data: otherRows } = await db
      .from("events")
      .select("slug, title, starts_at")
      .eq("tenant_id", event.tenant_id)
      .eq("status", "planned")
      .eq("visibility", "public")
      .neq("id", event.id)
      .gt("starts_at", new Date().toISOString())
      .order("starts_at", { ascending: true })
      .limit(3);

    return {
      already: rsvp.status === "cancelled",
      status: rsvp.status,
      firstName: pilot?.preferred_name ?? pilot?.first_name ?? null,
      guestCount: rsvp.guest_count ?? 0,
      eventTitle: event.title,
      eventSlug: event.slug,
      eventStartsAt: event.starts_at,
      alternatives: ((otherRows ?? []) as { slug: string; title: string; starts_at: string }[]).map(
        (e) => ({ slug: e.slug, title: e.title, startsAt: e.starts_at }),
      ),
    };
  });
